import { useEffect, useRef, useState } from 'preact/hooks'
import { ConfirmationModal } from '@/components/modals/ConfirmationModal'
import { SlugInput } from '@/components/common/SlugInput'
import { websApi } from '@/api/webs'
import { isValidSlugFormat, suggestWebSlug, webSlugCandidates, WEB_SLUG_MAX_LENGTH } from '@/utils/slugify'
import { formatWallClockShortDay, wallClock } from '@/utils/webinarRoomSetup'

interface Props {
  isOpen: boolean
  onClose: () => void
  onConfirm: (slug?: string) => void
  loading: boolean
  hasIntegration?: boolean
  /** The web already has a BigMarker event: imported, or signed off before. Sign-off then updates it rather than creating one. */
  hasBigMarkerEvent?: boolean
  /** The web runs in the OrbitalX room, so sign-off readies the room and touches nothing in BigMarker (ORB-1086). */
  isRoom?: boolean
  workspaceId: string
  webId: string
  /**
   * True for a Download web with a working or live quiz, the only web whose sign-off can publish a landing page: when it
   * changes the live quiz and a published page needs its newer version to follow (ORB-916). The box then asks the API
   * what it will publish.
   */
  mayPublishLandingPage: boolean
  /** Slug already stored on the web, if any. */
  storedSlug?: string | null
  /** Public title the suggestion is derived from. Undefined until one is set. */
  publicTitle?: string
  /** The online event's start on its own wall clock. Its date tells apart webs that share a title, as a re-run does its source's. */
  eventStart?: string | null
  /**
   * True once the web has ever been signed off. The address is fixed from that point, because the
   * BigMarker conference already carries it and nothing can push a correction (ORB-482).
   */
  frozen: boolean
}

/**
 * Sign-off confirmation, and the moment the public address is chosen.
 *
 * The slug lives here rather than in Settings because that is what the team asked for: decided at the
 * end, after the client conversation, not derived silently at creation. It is pre-filled from the
 * title so the common path is one click, but somebody has to look at it — this is the last screen
 * before the address reaches BigMarker and stops being changeable.
 */
export function WebSignOffModal({
  isOpen,
  onClose,
  onConfirm,
  loading,
  hasIntegration,
  hasBigMarkerEvent,
  isRoom,
  workspaceId,
  webId,
  mayPublishLandingPage,
  storedSlug,
  publicTitle,
  eventStart,
  frozen,
}: Props) {
  const [slug, setSlug] = useState('')
  const [suggesting, setSuggesting] = useState(false)

  /**
   * Mirrors `slug` synchronously so confirm reads the committed value rather than the one captured
   * when the handler was created.
   *
   * `SlugInput` propagates on a 300ms debounce and flushes on blur, and blur queues a state update
   * that has not necessarily re-rendered by the time the click handler runs. Typing and immediately
   * pressing Sign Off could therefore commit the previous value — and this value becomes a permanent
   * public URL pushed to BigMarker, so "usually fine" is not a good enough guarantee (ORB-482).
   */
  const slugRef = useRef('')
  const applySlug = (next: string) => {
    slugRef.current = next
    setSlug(next)
  }

  // Seed each time the panel opens rather than on mount: the title or the stored slug can change
  // while the page is up, and a stale suggestion here would be committed on confirm. The suggestion
  // is one the API says is free, so a re-run doesn't open on its source's address, already taken.
  // The field waits for it, so a late answer can't overwrite what someone typed.
  useEffect(() => {
    if (!isOpen) return
    if (storedSlug || frozen) {
      applySlug(storedSlug || '')
      setSuggesting(false)
      return
    }
    let current = true
    applySlug('')
    setSuggesting(true)
    const start = wallClock(eventStart)
    void suggestWebSlug(publicTitle, start && formatWallClockShortDay(start), async (candidate) => {
      const res = await websApi.checkSlug(workspaceId, candidate)
      if (!res.data) throw new Error(res.error ?? 'Unknown error')
      return res.data.available
    })
      // Where the API can't be asked, the title's own address, which the field's check then reports on.
      .catch(() => webSlugCandidates(publicTitle)[0] ?? '')
      .then((suggestion) => {
        if (!current) return
        applySlug(suggestion)
        setSuggesting(false)
      })
    return () => {
      current = false
    }
  }, [isOpen, storedSlug, frozen, publicTitle, eventStart, workspaceId])

  // Null while the read is in flight, so Sign Off waits until the box has said what it will publish.
  const [publications, setPublications] = useState<string[] | null>(null)
  const [publicationsError, setPublicationsError] = useState<string>()
  useEffect(() => {
    if (!isOpen || !mayPublishLandingPage) return
    let current = true
    setPublications(null)
    setPublicationsError(undefined)
    void websApi.getSignOffPreview(workspaceId, webId).then((res) => {
      if (!current) return
      if (res.data) setPublications(res.data.Publications)
      else setPublicationsError(res.error ?? 'Unknown error')
    })
    return () => {
      current = false
    }
  }, [isOpen, mayPublishLandingPage, workspaceId, webId])
  const readingPublications = mayPublishLandingPage && publications === null && !publicationsError

  const collectsSlug = !frozen
  const slugValid = isValidSlugFormat(slug.trim(), WEB_SLUG_MAX_LENGTH)

  const message = isRoom
    ? "Signing off sets this web's viewer link and gets its room ready. The recording and polls can't change until the web is reopened."
    : !hasIntegration
    ? 'This action confirms the web is ready.'
    : hasBigMarkerEvent
      ? "Signing off updates this web's event in BigMarker with its details and speakers."
      : "Signing off creates this web's event in BigMarker and pushes its details and speakers to it."


  return (
    <ConfirmationModal
      isOpen={isOpen}
      title="Sign Off Web"
      message={message}
      type="info"
      confirmText="Sign Off"
      onConfirm={() => onConfirm(collectsSlug ? slugRef.current.trim() : undefined)}
      onCancel={onClose}
      loading={loading}
      confirmDisabled={(collectsSlug && (suggesting || !slugValid)) || readingPublications}
    >
      {mayPublishLandingPage && publications?.map((publication, index) => (
        <p key={index} className="mt-3 text-sm text-slate-600 dark:text-slate-300 leading-relaxed">
          {publication}
        </p>
      ))}
      {publicationsError && (
        <p className="mt-3 text-sm text-red-600 dark:text-red-400 leading-relaxed">
          Couldn't check whether signing off also publishes the landing page: {publicationsError}
        </p>
      )}
      {collectsSlug && (
        <div className="mt-4">
          <SlugInput
            label="Public address"
            maxLength={WEB_SLUG_MAX_LENGTH}
            required
            value={slug}
            onChange={applySlug}
            disabled={suggesting}
            placeholder={suggesting ? 'Finding an address that is free…' : undefined}
            checkAvailability={async (candidate) => {
              if (candidate === storedSlug) return { available: true }
              const res = await websApi.checkSlug(workspaceId, candidate)
              return res.data ?? { available: false }
            }}
            helperText={
              publicTitle
                ? 'Suggested from the event title. Change it now if you want something else — it is fixed after sign-off.'
                : 'This becomes the public URL for the landing page and the event. It is fixed after sign-off.'
            }
          />
        </div>
      )}
    </ConfirmationModal>
  )
}
